import moment from "moment";
import { normalizeRegisterDobDisplayValue } from "../../Register/Register.validator";
import Encryption from "../../../api/Encryption";

export const getInsuranceOptionLabel = (option, t) =>
  option?.labelKey ? t(option.labelKey) : option?.label ?? "";

export const INSURANCE_PROVIDER_PLACEHOLDER = {
  value: "",
  labelKey: "NUTRITION_INSDISC_PLACEHOLDER_SELECT_INSURANCE",
};

export const mapInsuranceListToProviderOptions = (list) => {
  if (!Array.isArray(list) || list.length === 0) {
    return [INSURANCE_PROVIDER_PLACEHOLDER];
  }

  const options = list
    .map((item) => ({
      value: String(item.id ?? ""),
      label: item.name ?? item.label ?? "",
    }))
    .filter((item) => item.value && item.label);

  return [INSURANCE_PROVIDER_PLACEHOLDER, ...options];
};

export const isInsuranceListApiSuccess = (res) =>
  res?.status === true || res?.status === 1 || res?.status === "1";

export const extractInsuranceListFromResponse = (res) => {
  const data = res?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object") {
    if (Array.isArray(data.list)) {
      return data.list;
    }
    if (Array.isArray(data.insurance_list)) {
      return data.insurance_list;
    }
    if (Array.isArray(data.insurances)) {
      return data.insurances;
    }
  }

  return [];
};

export const resolveProviderOptionValue = (
  providerValue,
  insuranceListItems = []
) => {
  if (!providerValue) {
    return "";
  }

  const asString = String(providerValue).trim();
  if (!asString) {
    return "";
  }

  if (!Array.isArray(insuranceListItems) || insuranceListItems.length === 0) {
    return asString;
  }

  const byId = insuranceListItems.find((item) => String(item.id) === asString);
  if (byId) {
    return asString;
  }

  const normalized = asString.toLowerCase();
  const byName = insuranceListItems.find((item) => {
    const name = String(item.name ?? item.label ?? "").toLowerCase();
    return (
      name === normalized ||
      name.includes(normalized) ||
      normalized.includes(name.split(" ")[0])
    );
  });

  return byName?.id != null ? String(byName.id) : asString;
};

export const fetchRegistrationInsuranceProviders = async ({
  registrationCode,
  language,
  apiService,
  signal,
}) => {
  const normalizedCode = String(registrationCode || "").trim();
  if (!normalizedCode) {
    throw new Error("Registration code is required to load insurance providers.");
  }

  const res = await apiService.insurenceList(normalizedCode, language, { signal });
  if (!isInsuranceListApiSuccess(res)) {
    throw new Error(res?.message || "NUTRITION_INSMANUAL_ERR_LOAD_PROVIDERS");
  }

  const list = extractInsuranceListFromResponse(res);
  const options = mapInsuranceListToProviderOptions(list);

  return {
    list,
    options,
  };
};

export const RELATIONSHIP_OPTIONS = [
  { value: "", labelKey: "NUTRITION_INSMANUAL_PLACEHOLDER_RELATIONSHIP" },
  { value: "self", labelKey: "SELF" },
  { value: "spouse", labelKey: "NUTRITION_INSMANUAL_LBL_RELATIONSHIP_SPOUSE_PARTNER" },
  { value: "dependent", labelKey: "DEPENDENT" },
];

export const getInsuranceProviderLabel = (payerId, providersList) => {
  if (!Array.isArray(providersList) || providersList.length === 0) {
    return "";
  }

  const match = providersList.find(
    (provider) => String(provider.value) === String(payerId)
  );
  return match?.label ?? "";
};

export const MANUAL_RELATIONSHIP_OPTIONS = [
  { value: "", labelKey: "NUTRITION_INSMANUAL_PLACEHOLDER_RELATIONSHIP" },
  { value: "self", labelKey: "SELF" },
  { value: "spouse", labelKey: "NUTRITION_INSMANUAL_LBL_RELATIONSHIP_SPOUSE_PARTNER" },
  { value: "dependent", labelKey: "DEPENDENT" },
];

const MANUAL_RELATIONSHIP_TO_CODE = {
  self: "18",
  spouse: "01",
  dependent: "19",
};

export const mapManualRelationshipToCode = (relationship) =>
  MANUAL_RELATIONSHIP_TO_CODE[relationship] ?? "G8";

const VERIFICATION_RELATIONSHIP_TO_MANUAL = {
  18: "self",
  "01": "spouse",
  "19": "dependent",
};

export const normalizeVerificationRelationship = (relationship) => {
  const value = String(relationship ?? "").trim();
  const lowered = value.toLowerCase();
  if (lowered === "self" || lowered === "spouse" || lowered === "dependent") {
    return lowered;
  }
  return (
    VERIFICATION_RELATIONSHIP_TO_MANUAL[value] ??
    VERIFICATION_RELATIONSHIP_TO_MANUAL[lowered] ??
    ""
  );
};

export const mapVerificationRelationshipToManual = (relationship) =>
  normalizeVerificationRelationship(relationship);

const EMPTY_INSURANCE_MANUAL_FORM = {
  provider: "",
  memberId: "",
  groupNumber: "",
  relationship: "",
  holderFirstName: "",
  holderLastName: "",
  holderDob: "",
};

const hydratePolicyHolderNames = (manual = {}) => {
  if (manual.holderFirstName || manual.holderLastName) {
    return {
      holderFirstName: String(manual.holderFirstName || "").trim(),
      holderLastName: String(manual.holderLastName || "").trim(),
    };
  }

  if (manual.holderName) {
    const { first_name, last_name } = splitFullName(manual.holderName);
    return {
      holderFirstName: first_name,
      holderLastName: last_name,
    };
  }

  return { holderFirstName: "", holderLastName: "" };
};

/** Map verification form / stored verification into the manual details form shape. */
export const buildInsuranceManualFromVerification = (verification = {}) => {
  const relationship = normalizeVerificationRelationship(
    verification.relationship
  );
  const isSelf = relationship === "self";
  const firstName = String(verification.firstName ?? "").trim();
  const lastName = String(verification.lastName ?? "").trim();

  return {
    provider: verification.payer ? String(verification.payer) : "",
    memberId: String(verification.memberId ?? "").trim(),
    groupNumber: String(verification.groupNumber ?? "").trim(),
    relationship,
    holderFirstName: isSelf ? "" : firstName,
    holderLastName: isSelf ? "" : lastName,
    holderDob: isSelf ? "" : formatDobForDateInput(verification.dob),
  };
};

const buildManualFormFromVerificationAndCoverage = (
  registrationData = {},
  insuranceListItems = []
) => {
  const verification = registrationData.insuranceVerification;
  const coverage = registrationData.coverageResult;
  if (!verification && !coverage) {
    return null;
  }

  const payerOrProvider =
    verification?.payer ||
    findPayerIdByProviderName(
      verification?.providerName || coverage?.payerName,
      registrationData
    );
  const relationship = normalizeVerificationRelationship(
    verification?.relationship
  );
  const isSelf = relationship === "self";

  return {
    ...EMPTY_INSURANCE_MANUAL_FORM,
    provider: resolveProviderOptionValue(payerOrProvider, insuranceListItems),
    memberId: verification?.memberId || coverage?.memberId || "",
    groupNumber: verification?.groupNumber || coverage?.groupNumber || "",
    relationship,
    holderFirstName: !isSelf && verification?.firstName ? verification.firstName : "",
    holderLastName: !isSelf && verification?.lastName ? verification.lastName : "",
    holderDob:
      !isSelf && verification?.dob ? formatDobForDateInput(verification.dob) : "",
  };
};

export const buildInsuranceManualFormFromRegistrationData = (
  registrationData = {},
  insuranceListItems = []
) => {
  const manual = registrationData.insuranceManual;
  const preferManual =
    Boolean(registrationData.insuranceRecheck) && Boolean(manual);

  if (preferManual) {
    return {
      ...EMPTY_INSURANCE_MANUAL_FORM,
      ...manual,
      ...hydratePolicyHolderNames(manual),
      provider: resolveProviderOptionValue(manual.provider, insuranceListItems),
    };
  }

  // Latest "Find My Coverage" verification must override stale insuranceManual
  // (e.g. older holder DOB from a previous manual edit).
  const fromVerification = buildManualFormFromVerificationAndCoverage(
    registrationData,
    insuranceListItems
  );
  if (fromVerification) {
    return fromVerification;
  }

  if (manual) {
    return {
      ...EMPTY_INSURANCE_MANUAL_FORM,
      ...manual,
      ...hydratePolicyHolderNames(manual),
      provider: resolveProviderOptionValue(manual.provider, insuranceListItems),
    };
  }

  return null;
};

export const findPayerIdByProviderName = (providerName, registrationData = {}) => {
  const normalized = String(providerName || "").trim().toLowerCase();
  if (!normalized) {
    return "";
  }

  const list = registrationData.insuranceListItems;
  if (!Array.isArray(list)) {
    return "";
  }

  const match = list.find((item) => {
    const name = String(item.name ?? item.label ?? "").toLowerCase();
    return (
      name === normalized ||
      name.includes(normalized) ||
      normalized.includes(name.split(" ")[0])
    );
  });

  return match?.id != null ? String(match.id) : "";
};

export const formatDobForRecheckApi = (dob) => {
  if (!dob) {
    return "";
  }
  const parsed = moment(dob, ["YYYY-MM-DD", "MM/DD/YYYY", "YYYYMMDD"], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(dob).trim();
};

export const formatDobForDateInput = (dob) => normalizeRegisterDobDisplayValue(dob);

export const splitFullName = (fullName) => {
  const trimmed = String(fullName || "").trim();
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) {
    return { first_name: trimmed, last_name: "" };
  }
  return {
    first_name: trimmed.slice(0, spaceIndex),
    last_name: trimmed.slice(spaceIndex + 1).trim(),
  };
};

export const resolveManualProviderInsuranceId = (providerValue, registrationData = {}) => {
  const resolved = resolveSelectedInsurancePayerId(providerValue, registrationData);
  if (resolved && !Number.isNaN(Number(resolved))) {
    return Number(resolved);
  }

  const normalized = String(providerValue || "").trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  const list = registrationData.insuranceListItems;
  if (!Array.isArray(list)) {
    return undefined;
  }

  const match = list.find((item) => {
    const name = String(item.name ?? item.label ?? "").toLowerCase();
    return (
      name === normalized ||
      name.includes(normalized) ||
      normalized.includes(name.split(" ")[0])
    );
  });

  return match?.id != null ? Number(match.id) : undefined;
};

export const buildRecheckInsuranceEstimatePayload = (manual, accountData = {}) => {
  if (
    !manual?.provider ||
    !manual?.memberId ||
    !manual?.relationship
  ) {
    return null;
  }

  const insuranceId = resolveManualProviderInsuranceId(
    manual.provider,
    accountData
  );
  if (!insuranceId || Number.isNaN(insuranceId)) {
    return null;
  }

  const relationship = String(manual.relationship).trim();

  const payload = {
    member_id: String(manual.memberId).trim(),
    insurance_id: insuranceId,
    relationship_to_policy_holder: relationship,
    first_name: String(accountData.firstName || "").trim(),
    last_name: String(accountData.lastName || "").trim(),
    dob: formatDobForRecheckApi(accountData.dob),
  };

  const groupNumber = String(manual.groupNumber ?? "").trim();
  if (groupNumber) {
    payload.group_number = groupNumber;
  }

  if (relationship === "self") {
    payload.policy_holder_first_name = payload.first_name;
    payload.policy_holder_last_name = payload.last_name;
    payload.policy_holder_dob = payload.dob;
  } else {
    const { holderFirstName, holderLastName } = hydratePolicyHolderNames(manual);
    const holderDob = formatDobForRecheckApi(manual.holderDob);
    if (!holderFirstName || !holderLastName || !holderDob) {
      return null;
    }

    payload.policy_holder_first_name = holderFirstName;
    payload.policy_holder_last_name = holderLastName;
    payload.policy_holder_dob = holderDob;
  }

  return payload;
};

export const resolveSelectedInsurancePayerId = (
  selectedValue,
  registrationData = {}
) => {
  if (!selectedValue) {
    return "";
  }

  const list = registrationData.insuranceListItems;
  if (Array.isArray(list)) {
    const byId = list.find((item) => String(item.id) === String(selectedValue));
    if (byId?.id != null) {
      return String(byId.id);
    }

    const byName = list.find(
      (item) =>
        String(item.name ?? item.label ?? "").toLowerCase() ===
        String(selectedValue).toLowerCase()
    );
    if (byName?.id != null) {
      return String(byName.id);
    }
  }

  return String(selectedValue);
};

export const getAccountMemberFields = (registrationData = {}) => ({
  firstName: String(registrationData.firstName ?? "").trim(),
  lastName: String(registrationData.lastName ?? "").trim(),
  dob: formatDobForRecheckApi(registrationData.dob),
});

export const buildVerifyEligibilityRequest = (
  values,
  registrationData = {}
) => {
  const insuranceId = resolveSelectedInsurancePayerId(
    values.payer,
    registrationData
  );
  if (!insuranceId || Number.isNaN(Number(insuranceId))) {
    return null;
  }

  const relationship = String(values.relationship ?? "").trim();
  const memberId = String(values.memberId ?? "").trim();
  const groupNumber = String(values.groupNumber ?? "").trim();
  const accountMember = getAccountMemberFields(registrationData);

  if (
    !memberId ||
    !relationship ||
    !accountMember.firstName ||
    !accountMember.lastName ||
    !accountMember.dob
  ) {
    return null;
  }

  const payload = {
    member_id: memberId,
    insurance_id: Number(insuranceId),
    relationship_to_policy_holder: relationship,
    first_name: accountMember.firstName,
    last_name: accountMember.lastName,
    dob: accountMember.dob,
  };

  if (groupNumber) {
    payload.group_number = groupNumber;
  }

  if (relationship === "self") {
    payload.policy_holder_first_name = accountMember.firstName;
    payload.policy_holder_last_name = accountMember.lastName;
    payload.policy_holder_dob = accountMember.dob;
    return payload;
  }

  const policyHolderFirstName = String(values.firstName ?? "").trim();
  const policyHolderLastName = String(values.lastName ?? "").trim();
  const policyHolderDob = formatDobForRecheckApi(values.dob);

  if (!policyHolderFirstName || !policyHolderLastName || !policyHolderDob) {
    return null;
  }

  payload.policy_holder_first_name = policyHolderFirstName;
  payload.policy_holder_last_name = policyHolderLastName;
  payload.policy_holder_dob = policyHolderDob;

  return payload;
};

export const buildVerifyEligibilityRequestFromVerification = (
  verification = {},
  registrationData = {}
) =>
  buildVerifyEligibilityRequest(
    {
      payer: verification.payer,
      memberId: verification.memberId,
      groupNumber: verification.groupNumber,
      firstName: verification.firstName,
      lastName: verification.lastName,
      dob: verification.dob,
      relationship: normalizeVerificationRelationship(verification.relationship),
    },
    registrationData
  );

export const buildVerifyEligibilityFormData = (
  verification = {},
  registrationData = {},
  cardImages = {}
) => {
  const payload = buildVerifyEligibilityRequestFromVerification(
    verification,
    registrationData
  );
  if (!payload) {
    return null;
  }
  if (
    !(cardImages.frontFile instanceof File) ||
    !(cardImages.backFile instanceof File)
  ) {
    return null;
  }

  const formData = new FormData();
  const encrypted = Encryption.encrypt(payload);
  formData.append("data", encrypted.data);
  formData.append("data2", cardImages.frontFile);
  formData.append("data3", cardImages.backFile);

  return formData;
};

export const buildRecheckInsuranceEstimateFormData = (
  manual = {},
  accountData = {},
  cardImages = {}
) => {
  const payload = buildRecheckInsuranceEstimatePayload(manual, accountData);
  if (!payload) {
    return null;
  }
  if (
    !(cardImages.frontFile instanceof File) ||
    !(cardImages.backFile instanceof File)
  ) {
    return null;
  }

  const formData = new FormData();
  const encrypted = Encryption.encrypt(payload);
  formData.append("data", encrypted.data);
  formData.append("data2", cardImages.frontFile);
  formData.append("data3", cardImages.backFile);

  return formData;
};

export const isEligibilityVerified = (eligibilityResult) => {
  if (!eligibilityResult || typeof eligibilityResult !== "object") {
    return false;
  }
  const verified =
    eligibilityResult.verified ?? eligibilityResult.is_verified;
  return verified === true || verified === 1 || verified === "1";
};

const pickNonEmptyString = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
};

export const getInsuranceVerificationFailureMessage = (
  res,
  eligibilityResult = {}
) => {
  const data =
    res?.data && typeof res.data === "object" && !Array.isArray(res.data)
      ? res.data
      : {};

  return pickNonEmptyString(
    res?.message,
    data.message,
    eligibilityResult.message,
    eligibilityResult.verification_message,
    eligibilityResult.verificationMessage,
    eligibilityResult.reason,
    eligibilityResult.error_message,
    eligibilityResult.errorMessage,
    eligibilityResult.description
  );
};

export const parseIsCardPresent = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const getIsCardPresentFromEligibility = (eligibilityResult) => {
  if (!eligibilityResult || typeof eligibilityResult !== "object") {
    return false;
  }
  return parseIsCardPresent(
    eligibilityResult.is_card_present ?? eligibilityResult.isCardPresent
  );
};

/** API may return status:false with verified:false — still a valid eligibility outcome. */
export const parseVerifyEligibilityResponse = (res) => {
  if (!res || typeof res !== "object") {
    return {
      ok: false,
      errorMessage: "NUTRITION_INSDISC_ERR_VERIFY_COVERAGE",
    };
  }

  const data =
    res.data && typeof res.data === "object" ? res.data : {};
  const hasVerifiedField =
    Object.prototype.hasOwnProperty.call(data, "verified") ||
    Object.prototype.hasOwnProperty.call(data, "is_verified") ||
    Object.prototype.hasOwnProperty.call(res, "verified") ||
    Object.prototype.hasOwnProperty.call(res, "is_verified");

  if (hasVerifiedField) {
    return {
      ok: true,
      eligibilityResult: {
        ...data,
        verified:
          data.verified ?? data.is_verified ?? res.verified ?? res.is_verified,
      },
    };
  }

  if (res.status) {
    return { ok: true, eligibilityResult: data };
  }

  return {
    ok: false,
    errorMessage:
      res.message || "NUTRITION_INSDISC_ERR_VERIFY_COVERAGE",
  };
};
